import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { Layers } from '@modules/core';
import { useZed } from '@modules/core/src/react/useZed';
import { StandardModule } from '@modules/standard/src/StandardModule';
import { zams } from '@zams/logger/zed/generated/loggerMap';
import { useEffect, useMemo, useRef, useState } from 'react';

import { MeshType } from '../ecs/component/data/DanjiMesh';
import { RoomTypeResponse } from '../ecs/component/response/RoomType';
import { getTourDanjiMetaData, TourDanjiModule } from '../TourDanjiModule';

import { AreaUnitChangeButton } from './AreaUnitChangeButton';
import arrow_icon from './assets/arrow_down.png';
import { CompassPanel } from './CompassPanel';
import { useScreen } from './hooks/useScreen';
import styles from './styles/RoomTypeFilter.module.css';

const CAMERA_SETTLE_DELAY_MS = 300;

export const RoomTypeFilter = () => {
  const [roomTypes, setRoomTypes] = useState<RoomTypeResponse[]>([]);
  const [isAll, setIsAll] = useState(false);
  const [isFold, setIsFold] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const [useMeterAreaUnit, setMeterAreaUnit] = useState(true);
  const [scrollTargetIndex, setScrollTargetIndex] = useState<number | null>(null);
  const [restoreFilters, setRestoreFilters] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const screenInfo = useScreen();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const zed = useZed<[BabylonjsModule, StandardModule, TourDanjiModule]>();

  const { isDesktop } = screenInfo;

  useEffect(() => {
    if (!isDesktop) return;
    const scrollContainer = listRef.current;
    if (!scrollContainer) return;

    let isDown = false;
    let startX: number;
    let scrollLeft: number;

    const handleMouseDown = (e: MouseEvent) => {
      isDown = true;
      scrollContainer.classList.add(styles.active);
      startX = e.pageX - scrollContainer.offsetLeft;
      scrollLeft = scrollContainer.scrollLeft;
    };

    const handleMouseLeave = () => {
      isDown = false;
      scrollContainer.classList.remove(styles.active);
    };

    const handleMouseUp = () => {
      isDown = false;
      scrollContainer.classList.remove(styles.active);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - scrollContainer.offsetLeft;
      const walk = (x - startX) * 2; // 스크롤 속도 조절
      scrollContainer.scrollLeft = scrollLeft - walk;
    };

    scrollContainer.addEventListener('mousedown', handleMouseDown);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);
    scrollContainer.addEventListener('mouseup', handleMouseUp);
    scrollContainer.addEventListener('mousemove', handleMouseMove);

    return () => {
      scrollContainer.removeEventListener('mousedown', handleMouseDown);
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
      scrollContainer.removeEventListener('mouseup', handleMouseUp);
      scrollContainer.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {
    if (!isFold && scrollTargetIndex !== null && listRef.current) {
      const targetChild = listRef.current.children[scrollTargetIndex + 1]; // +1 for 전체 타입 button
      if (targetChild) {
        setTimeout(() => {
          targetChild.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150); // CSS 애니메이션 시간에 맞춰 스크롤
      }
      setScrollTargetIndex(null);
    }
  }, [isFold, scrollTargetIndex]);

  // Fade out immediately when unit view starts changing
  useEffect(() => {
    if (!zed) return;
    const observer = zed.api.observer.onUnitViewStateChanged.add(() => {
      setIsTransitioning(true);
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
    });
    return () => {
      observer?.remove();
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
    };
  }, [zed]);

  useEffect(() => {
    if (!zed) return;

    const onCameraViewTypeChanged = zed.api.observer.onCameraViewTypeChanged.add((data) => {
      const isVisible = data !== 'UnitWindowView' && data !== 'FakeUnitWindowView';
      if (isVisible) {
        setIsVisible(true);
        setRestoreFilters(true);
      } else {
        setIsVisible(false);
        zed?.api.setVisibleMeshes(MeshType.DONG_ROOMTYPE_WALL, false);
      }

      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
      if (data === 'Transition') {
        setIsTransitioning(true);
      } else {
        transitionTimerRef.current = setTimeout(() => {
          setIsTransitioning(false);
        }, CAMERA_SETTLE_DELAY_MS);
      }
    });

    const onUpdateRoomTypeFilter = zed.api.observer.onUpdateRoomTypeFilter.add((data) => {
      setRoomTypes(data);
    });

    return () => {
      onCameraViewTypeChanged.remove();
      onUpdateRoomTypeFilter.remove();
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
    };
  }, [zed]);

  // 세대뷰에서 복귀 시 최신 state 기반으로 3D 필터 복원
  useEffect(() => {
    if (!restoreFilters || !zed) return;
    const responses = isAll
      ? roomTypes.map((r) => ({ ...r, active: true }))
      : roomTypes;
    zed.api.updateRoomTypeResponse(responses);
    setRestoreFilters(false);
  }, [restoreFilters, roomTypes, isAll, zed]);

  const handleRoomTypeToggle = (index: number) => {
    const metaData = getTourDanjiMetaData();
    const response = roomTypes[index];

    let newResponses: RoomTypeResponse[];
    let status: 'on' | 'off';

    if (isAll) {
      // "전체 타입" 해제 → 클릭한 타입만 활성화 (fresh start)
      newResponses = roomTypes.map((r, idx) => ({
        ...r,
        active: idx === index,
      }));
      setIsAll(false);
      status = 'on';
    } else {
      // 개별 토글: 켜져 있으면 off, 꺼져 있으면 on
      status = !response.active ? 'on' : 'off';
      newResponses = roomTypes.map((r, idx) => {
        if (idx === index) {
          return { ...r, active: !r.active };
        }
        return r;
      });
    }

    zams
      .z133()
      .setAllParams({
        screen_name: metaData.screen_name,
        ...(metaData.deal_type && { deal_type: metaData.deal_type }),
        ...(metaData.item_type && { item_type: metaData.item_type }),
        ...(metaData.ho_id && { ho_id: metaData.ho_id }),
        area_danji_id: metaData.danjiId,
        dong: metaData.dong,
        ho: metaData.ho,
        item_id: metaData.itemId,
        status_value: useMeterAreaUnit ? response.roomTypeTitle.m2 : response.roomTypeTitle.p,
        status,
      })
      .send();

    zed?.api.updateRoomTypeResponse(newResponses);
    setRoomTypes(newResponses);
  };

  const toggleAll = () => {
    const isOn = !isAll;
    const metaData = getTourDanjiMetaData();
    zams
      .z133()
      .setAllParams({
        screen_name: metaData.screen_name,
        ...(metaData.deal_type && { deal_type: metaData.deal_type }),
        ...(metaData.item_type && { item_type: metaData.item_type }),
        ...(metaData.ho_id && { ho_id: metaData.ho_id }),
        area_danji_id: metaData.danjiId,
        dong: metaData.dong,
        ho: metaData.ho,
        item_id: metaData.itemId,
        status_value: '전체',
        status: isOn ? 'on' : 'off',
      })
      .send();

    setIsAll(isOn);
    if (isOn) {
      // "전체 타입" ON: 3D에는 모든 타입 표시하지만 개별 칩은 비활성 (isAll로 제어)
      const allActiveResponses = roomTypes.map((r) => ({ ...r, active: true }));
      zed?.api.updateRoomTypeResponse(allActiveResponses);
      setRoomTypes(roomTypes.map((r) => ({ ...r, active: false })));
    } else {
      // "전체 타입" OFF: 모든 타입 비활성
      const responses = roomTypes.map((r) => ({ ...r, active: false }));
      zed?.api.updateRoomTypeResponse(responses);
      setRoomTypes(responses);
    }
  };

  const hasSelection = useMemo(() => isAll || roomTypes.some((r) => r.active), [roomTypes, isAll]);

  const triggerBarClass = isDesktop ? styles.triggerBarWWW : styles.triggerBar;
  const triggerContentClass = hasSelection ? styles.triggerContentSelected : styles.triggerContent;
  const expandedContainerClass = isDesktop ? styles.backgroundFrameExpandWWW : styles.backgroundFrameExpand;
  const listContainerClass = isDesktop ? styles.listContainerExpandWWW : styles.listContainerExpand;

  const renderChip = (response: RoomTypeResponse, index: number) => (
    <button
      key={response.roomTypeId}
      data-room-type={response.roomTypeId}
      className={response.active ? styles.roomTypeButtonActive : styles.roomTypeButton}
      onClick={() => {
        handleRoomTypeToggle(index);
      }}>
      <span className={styles.buttonContentContainer}>
        <span className={styles.colorIndicator} style={{ backgroundColor: response.color }} />
        <span className={styles.text}>
          {useMeterAreaUnit ? `${response.roomTypeTitle.m2}㎡` : `${response.roomTypeTitle.p}평`}
        </span>
      </span>
    </button>
  );

  const showUI = isVisible || isTransitioning;
  const fadeClass = (!isVisible || isTransitioning) ? styles.fadeOut : styles.fadeIn;

  return showUI && (
    <div style={{ zIndex: Layers.UI_CONTENTS + 1 }} className={`${styles.root} ${fadeClass}`}>
      <CompassPanel />
      <AreaUnitChangeButton
        useMeterAreaUnit={useMeterAreaUnit}
        onClick={() => {
          const metaData = getTourDanjiMetaData();
          zams
            .z132()
            .setAllParams({
              screen_name: metaData.screen_name,
              ...(metaData.deal_type && { deal_type: metaData.deal_type }),
              ...(metaData.item_type && { item_type: metaData.item_type }),
              ...(metaData.ho_id && { ho_id: metaData.ho_id }),
              area_danji_id: metaData.danjiId,
              dong: metaData.dong,
              ho: metaData.ho,
              item_id: metaData.itemId,
              status_value: useMeterAreaUnit ? '평' : 'm',
            })
            .send();
          setMeterAreaUnit(!useMeterAreaUnit);
        }}
      />

      {/* Trigger Bar - 항상 표시 */}
      <div className={triggerBarClass}>
        <div
          className={triggerContentClass}
          onClick={() => {
            if (isFold) {
              setIsFold(false);
              if (containerRef.current) {
                containerRef.current.scrollTop = 0;
              }
            }
          }}>
          {hasSelection ? (
            <div className={styles.triggerChipList}>
              {isAll ? (
                <button
                  className={styles.selectAllButtonActive}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isFold) setIsFold(false);
                  }}>
                  {'전체 타입'}
                </button>
              ) : (
                roomTypes.map((response, index) => {
                  if (!response.active) return null;
                  return (
                    <button
                      key={response.roomTypeId}
                      className={styles.roomTypeButtonActive}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isFold) {
                          setScrollTargetIndex(index);
                          setIsFold(false);
                        } else {
                          const chipEl = listRef.current?.querySelector(
                            `[data-room-type="${response.roomTypeId}"]`
                          ) as HTMLElement | null;
                          if (chipEl) {
                            chipEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                        }
                      }}>
                      <span className={styles.buttonContentContainer}>
                        <span className={styles.colorIndicator} style={{ backgroundColor: response.color }} />
                        <span className={styles.text}>
                          {useMeterAreaUnit ? `${response.roomTypeTitle.m2}㎡` : `${response.roomTypeTitle.p}평`}
                        </span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          ) : (
            <span className={styles.triggerPlaceholder}>
              <strong>{roomTypes.length}개</strong>
              {'의 타입 중 보고싶은 타입을 선택해주세요.'}
            </span>
          )}
        </div>
        <div className={`${styles.triggerRightFade} ${!hasSelection ? styles.triggerRightFadeHidden : ''}`} />
        <button
          className={styles.triggerToggleButton}
          onClick={() => {
            const nextFold = !isFold;
            setIsFold(nextFold);
            if (!nextFold && containerRef.current) {
              containerRef.current.scrollTop = 0;
            }
          }}>
          {isFold ? (
            <img src={arrow_icon} style={{ width: 18, height: 9.4 }} />
          ) : (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M14 4L4 14M4 4L14 14" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
          )}
        </button>
      </div>

      {/* 칩 컨테이너 (애니메이션으로 열림/닫힘) */}
      <div ref={containerRef} className={`${expandedContainerClass} ${isFold ? styles.expandCollapsed : ''}`}>
        <div ref={listRef} className={listContainerClass}>
          <button
            className={isAll ? styles.selectAllButtonActive : styles.selectAllButton}
            onClick={() => {
              toggleAll();
            }}>
            {'전체 타입'}
          </button>
          {roomTypes.map((response, index) => renderChip(response, index))}
        </div>
      </div>
    </div>
  );
};
